import React, { FormEvent, useMemo } from 'react';
import {
  Amount,
  Box,
  Button,
  Text,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Spinner,
  useToast,
} from '@razorpay/blade/components';
import { FormWrapper, StepActionsWrapper, StepContentWrapper, StepHeader } from '../components';
import { SummaryView, SummaryViewItem } from './components/SummaryView';
import { useFormWithProvider } from 'forms';
import { ReviseSalaryFormData, SALARY_PAY_TYPES } from './constants';
import { format } from 'date-fns';
import { dateFormats } from 'utils/Dates';
import { useHistory, Redirect } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import SalaryStructureDropDown from 'components/SalaryStructures/creation/components/SalaryStructureDropDown';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import api from 'api';
import { useMutation, useQuery } from 'react-query';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { getErrorsAsString } from 'components/Kyc/utils';
import { AppError } from 'utils/AppError';
import { transformBulkPreviewData } from './utils';
import { getSalaryStructureComponentFormulas } from 'components/SalaryStructures/utils';
import { parseGetSalaryStructureResponse } from 'components/SalaryStructure/utils';
import { salaryStructureComponentType } from 'components/SalaryStructure/constants';

type ComponentData = {
  id: string;
  name: string;
  formula: string;
  category: SalaryStructureComponent['salaryComponent']['category'];
  componentType: SalaryStructureComponent['salaryComponent']['componentType'];
};

type GroupedData = {
  id: string;
  name: string;
  formula: string;
  nodes: ComponentData[];
}[];

const UpdateStructureStep = () => {
  const history = useHistory();
  const toast = useToast();
  const { formData, register, setFormData, setFieldValue } =
    useFormWithProvider<ReviseSalaryFormData>();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId?.toString() ?? '';
  const currentSalaryStructureId = formData.salaryStructureId?.[0] || null;

  const { isFetching: isCurrentStructureLoading, data: selectedStructureDetails } = useQuery({
    queryKey: [salaryStructureQueries.getSalaryStructure, currentSalaryStructureId],
    queryFn: async () =>
      api.salaryStructures.queries.getSalaryStructure({
        input: {
          id: currentSalaryStructureId!,
        },
      }),
    select: (data) => {
      const response = parseGetSalaryStructureResponse(data);
      return response.payrollGetSalaryStructure.data;
    },
    enabled: !!currentSalaryStructureId,
  });

  const isSalaryStructureUpdated =
    formData.salaryStructureForUserInDb?.id !== formData.salaryStructureId?.[0];

  const getFormattedComponents = () => {
    if (!selectedStructureDetails?.salaryStructureComponents) {
      return [];
    }

    const earnings: ComponentData[] = [];
    const variables: ComponentData[] = [];
    const nonPayableBenefits: ComponentData[] = [];

    selectedStructureDetails.salaryStructureComponents.forEach((component) => {
      if (!component.salaryComponent?.name) {
        return;
      }

      const componentData = {
        id: component.id,
        name: component.salaryComponent?.name,
        formula: getSalaryStructureComponentFormulas(component),
        category: component.salaryComponent?.category,
        componentType: component.salaryComponent?.componentType,
      };

      const payType =
        component.salaryComponent?.settings &&
        'earningDetails' in component.salaryComponent?.settings
          ? component.salaryComponent?.settings?.earningDetails?.payType || ''
          : '';

      if (payType === SALARY_PAY_TYPES.FIXED) {
        earnings.push(componentData);
      } else if (payType === SALARY_PAY_TYPES.VARIABLE) {
        variables.push(componentData);
      } else {
        nonPayableBenefits.push(componentData);
      }
    });

    const sortedEarnings = earnings.sort((componentA, componentB) => {
      if (componentA.componentType === salaryStructureComponentType.COMPONENT_TYPE_SPECIAL_ALLOWANCE) {
        return 1;
      }
      if (componentB.componentType === salaryStructureComponentType.COMPONENT_TYPE_SPECIAL_ALLOWANCE) {
        return -1;
      }
      return 0;
    });

    const groupedData: GroupedData = [];

    if (sortedEarnings.length > 0) {
      groupedData.push({
        id: 'earnings-group',
        name: 'Earning',
        formula: '',
        nodes: sortedEarnings.map((item) => ({ ...item, nodes: null })),
      });
    }

    if (variables.length > 0) {
      groupedData.push({
        id: 'variables-group',
        name: 'Variable',
        formula: '',
        nodes: variables.map((item) => ({ ...item, nodes: null })),
      });
    }

    if (nonPayableBenefits.length > 0) {
      groupedData.push({
        id: 'non-payable-group',
        name: 'Non-payable benefit',
        formula: '',
        nodes: nonPayableBenefits.map((item) => ({ ...item, nodes: null })),
      });
    }

    const components = groupedData.flatMap((group) => [
      { ...group, isGroupHeader: true },
      ...(group.nodes || []).map((item) => ({ ...item, isGroupHeader: false })),
    ]);

    return components;
  };

  const components = useMemo(getFormattedComponents, [
    selectedStructureDetails?.salaryStructureComponents,
  ]);

  const bulkPreviewMutation = useMutation({
    mutationFn: api.userQueries.getPayDetailsBulkPreview,
    onSuccess: (response) => {
      const bulkPreviewData = response?.payrollGetPayDetailsBulkPreview?.data?.revisionDetails?.[0];
      if (bulkPreviewData) {
        const transformedData = transformBulkPreviewData({
          bulkPreviewData,
          requestId: response?.payrollGetPayDetailsBulkPreview?.data?.requestId,
          isSalaryStructureUpdated,
        });

        setFormData({ ...formData, ctcData: transformedData });
        history.push({
          pathname: routePaths.payDetails.compensation.edit.review,
          search: `?userId=${userId}`,
        });
      }
    },
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  if (!formData.revisedCompensation || !formData.effectiveFrom) {
    return (
      <Redirect
        to={{
          pathname: routePaths.payDetails.compensation.edit.salaryDetails,
          search: `?userId=${userId}`,
        }}
      />
    );
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    bulkPreviewMutation.mutate({
      revisionRequests: [
        {
          userId: parseInt(userId),
          annualSalary: parseInt(formData.revisedCompensation ?? '0'),
          effectiveFrom: formData.effectiveFrom
            ? format(new Date(formData.effectiveFrom), dateFormats.yearMonthDate)
            : '',
          salaryStructureId: currentSalaryStructureId || '',
          hasStructureChange: isSalaryStructureUpdated,
        },
      ],
    });
  };

  const isLoading = isCurrentStructureLoading || bulkPreviewMutation.isLoading;

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper maxWidth="600px" noBgIllustration>
        <SummaryView>
          <SummaryViewItem label="Revised CTC">
            <Amount
              size="large"
              value={parseInt(formData.revisedCompensation ?? '0')}
              suffix="none"
            />
          </SummaryViewItem>
          <SummaryViewItem label="Monthly CTC">
            <Amount
              size="large"
              value={parseInt(formData.revisedCompensation ?? '0') / 12}
              suffix="none"
            />
          </SummaryViewItem>
          <SummaryViewItem label="Effective from">
            <Text size="large">
              {formData.effectiveFrom ? format(formData.effectiveFrom, 'dd MMM, yyyy') : ''}
            </Text>
          </SummaryViewItem>
        </SummaryView>

        <Box display="flex" flexDirection="column" gap="spacing.6" marginTop="spacing.8">
          <Box
            display="flex"
            flexDirection="column"
            borderWidth="thin"
            borderColor="surface.border.gray.muted"
            borderRadius="medium"
            padding={'spacing.7'}
            gap="spacing.6">
            <Box display="flex" flexDirection="column" gap="spacing.4">
              <StepHeader title="Update structure" subtitle="" />
              <Text variant="body" weight="regular" size="medium" color="surface.text.gray.subtle">
                The updated structure applies to all unfinalised payrolls; arrears will be based on
                the revised structure.{' '}
              </Text>
            </Box>
            <SalaryStructureDropDown
              structures={formData.salaryStructureData}
              registerProps={register('salaryStructureId', 'select')}
              hasAutoComplete={true}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            borderWidth="thin"
            borderColor="surface.border.gray.muted"
            borderRadius="medium"
            padding={'spacing.7'}
            gap="spacing.6">
            <StepHeader
              title={`Structure preview  ${
                selectedStructureDetails?.name
                  ? selectedStructureDetails?.type === 'SALARY_STRUCTURE_TYPE_DEFAULT'
                    ? ''
                    : `| ${selectedStructureDetails?.name}`
                  : ''
              }`}
              subtitle=""
            />
            <Box display="flex" flexDirection="column" borderRadius="medium" overflow="hidden">
              {isCurrentStructureLoading ? (
                <Box padding="spacing.8" display="flex" justifyContent="center" alignItems="center">
                  <Spinner accessibilityLabel="Loading structure preview" />
                </Box>
              ) : components.length > 0 ? (
                <Table data={{ nodes: components }} gridTemplateColumns="1fr 1fr">
                  {(tableData) => {
                    return (
                      <>
                        <TableHeader>
                          <TableHeaderRow>
                            <TableHeaderCell>
                              <Text weight="semibold">Component</Text>
                            </TableHeaderCell>
                            <TableHeaderCell>
                              <Text weight="semibold">Calculation</Text>
                            </TableHeaderCell>
                          </TableHeaderRow>
                        </TableHeader>
                        <TableBody>
                          {tableData.map((item) => {
                            const isGroup = item.isGroupHeader;
                            if (isGroup) {
                              return (
                                <TableRow key={item.id} item={item}>
                                  <TableCell>
                                    <Text
                                      weight="semibold"
                                      color="surface.text.gray.muted"
                                      size="small">
                                      {item.name}
                                    </Text>
                                  </TableCell>
                                  <TableCell>
                                    <Box />
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            return (
                              <TableRow key={item.id} item={item}>
                                <TableCell>
                                  <Text>{item.name}</Text>
                                </TableCell>
                                <TableCell>
                                  <Text>{item.formula}</Text>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </>
                    );
                  }}
                </Table>
              ) : (
                <Box padding="spacing.8">
                  <Text color="surface.text.gray.muted">No data found...</Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StepContentWrapper>
      <StepActionsWrapper maxWidth="600px">
        <Button variant="secondary" onClick={() => history.goBack()} isDisabled={isLoading}>
          Back
        </Button>
        <Button type="submit" isLoading={isLoading}>
          Next
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default UpdateStructureStep;
